import { useState } from 'react';
import { Modal, Button, Form, Alert } from 'react-bootstrap';
import { Truck, Undo2 } from 'lucide-react';

interface ChangeTrackingNumberModalProps {
  readonly show: boolean;
  readonly onHide: () => void;
  readonly currentProviderOrderId?: string | undefined;
  readonly onConfirm: (newProviderOrderId: string) => Promise<{ success: boolean; error?: string }>;
}

export function ChangeTrackingNumberModal({
  show,
  onHide,
  currentProviderOrderId,
  onConfirm,
}: ChangeTrackingNumberModalProps) {
  const [newNumber, setNewNumber] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  // The number that was in place before the failed change — captured so the
  // operator can explicitly revert if the new number turns out to be a typo.
  const [revertTarget, setRevertTarget] = useState<string | null>(null);

  const trimmed = newNumber.trim();
  // Allow submit for a genuinely new number, or to retry after a failure
  // (refresh failures can be transient, so re-running the same number is valid).
  const canSubmit = trimmed.length > 0 && !submitting && (trimmed !== currentProviderOrderId || errorMsg !== null);

  const handleClose = () => {
    if (submitting) {
      return;
    }
    setNewNumber('');
    setErrorMsg(null);
    setRevertTarget(null);
    onHide();
  };

  const runConfirm = async (value: string) => {
    setSubmitting(true);
    setErrorMsg(null);
    try {
      const result = await onConfirm(value);
      if (result.success) {
        setNewNumber('');
        setRevertTarget(null);
        onHide();
      } else {
        // Step 1 (PATCH) succeeded but the refresh could not resolve the
        // number — keep the modal open so the operator can correct, retry,
        // or revert.
        setErrorMsg(result.error ?? 'Tracking refresh failed. Please verify the number and retry.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async () => {
    if (!canSubmit) {
      return;
    }
    // Snapshot the number being replaced before onConfirm changes it.
    if (currentProviderOrderId && currentProviderOrderId !== trimmed) {
      setRevertTarget(currentProviderOrderId);
    }
    await runConfirm(trimmed);
  };

  const handleRevert = async () => {
    if (!revertTarget || submitting) {
      return;
    }
    await runConfirm(revertTarget);
  };

  return (
    <Modal show={show} onHide={handleClose}>
      <Modal.Header closeButton>
        <Modal.Title className="d-flex align-items-center gap-2">
          <Truck size={20} />
          Change YunExpress Tracking Number
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="alert alert-warning" role="alert">
          <strong>⚠️ Note:</strong> Saving replaces the YunExpress waybill number and re-fetches tracking. If the new
          number cannot be found at YunExpress, the number is still changed but tracking will not update — verify the
          number and retry, or revert to the previous number.
        </div>

        <Form.Group className="mb-3">
          <Form.Label>Current Tracking Number</Form.Label>
          <Form.Control type="text" value={currentProviderOrderId || '-'} disabled />
        </Form.Group>

        <Form.Group>
          <Form.Label>
            New Tracking Number <span className="text-danger">*</span>
          </Form.Label>
          <Form.Control
            type="text"
            value={newNumber}
            onChange={(e) => setNewNumber(e.target.value)}
            placeholder="Enter new YunExpress waybill number"
            disabled={submitting}
            autoFocus
          />
        </Form.Group>

        {errorMsg && (
          <Alert variant="danger" className="mt-3 mb-0">
            <div>{errorMsg}</div>
            {revertTarget && (
              <div className="mt-1 small text-muted">
                You can revert to the previous number <code>{revertTarget}</code> if the new number was entered by
                mistake.
              </div>
            )}
          </Alert>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>
        {errorMsg && revertTarget && (
          <Button variant="outline-warning" onClick={handleRevert} disabled={submitting}>
            <Undo2 className="h-4 w-4 me-1" />
            Revert to {revertTarget}
          </Button>
        )}
        <Button variant="primary" onClick={handleSubmit} disabled={!canSubmit}>
          {submitting ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              Saving...
            </>
          ) : (
            'Save & Refresh'
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
